import { useRef, useState } from 'react';
import { X, UploadSimple, FileText } from '@phosphor-icons/react';

export const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp';

interface DocumentDropZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** false: one file, and a new pick replaces it. */
  multiple?: boolean;
  disabled?: boolean;
  title?: string;
  hint?: string;
  /** Less padding, for the proposal side panel. */
  compact?: boolean;
}

/** Click-or-drag upload area with the picked files listed below it (shared by create and the side panel). */
export function DocumentDropZone({
  files, onChange, multiple = true, disabled, compact,
  title = 'Click to upload or drag the proposal here',
  hint = 'PDF, Word, PowerPoint or images (scans are fine) — stored with the proposal',
}: DocumentDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const add = (list: FileList | null) => {
    if (!list || list.length === 0 || disabled) return;
    const picked = Array.from(list);
    onChange(multiple ? [...files, ...picked] : picked.slice(0, 1));
  };

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); add(e.dataTransfer.files); }}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        className={`border-2 border-dashed rounded-2xl ${compact ? 'p-4' : 'p-8'} text-center transition-all ${
          disabled ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50/50'
            : isDragging ? 'cursor-pointer border-accent bg-accent/5'
              : 'cursor-pointer border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={DOCUMENT_ACCEPT}
          className="hidden"
          disabled={disabled}
          onChange={(e) => { add(e.target.files); e.target.value = ''; }}
        />
        <div className="flex flex-col items-center gap-1">
          <UploadSimple className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-accent`} />
          <p className="text-xs font-semibold text-slate-700">{title}</p>
          <p className="text-[9px] text-slate-400">{hint}</p>
        </div>
      </div>
      {files.map((file, idx) => (
        <div key={`${file.name}-${idx}`} className="flex items-center justify-between bg-slate-50 border border-slate-200/60 px-3 py-1.5 rounded-xl text-xs">
          <div className="flex items-center gap-2 min-w-0 pr-2">
            <FileText className="w-4 h-4 text-accent shrink-0" />
            <span className="font-medium text-slate-700 truncate">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => onChange(files.filter((_, j) => j !== idx))}
            disabled={disabled}
            className="text-slate-400 hover:text-rose-500 transition-colors p-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
